/* eslint-disable react/prop-types */
import { useState } from "react";
import { gql, useApolloClient, useQuery } from "@apollo/client";
import { PDFDocument } from "pdf-lib";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlined from "@mui/icons-material/PictureAsPdfOutlined";
import {
  Alert,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Campo } from "./Formulario";
import { documentBlob, download, saveBlob } from "./api";
import { empty } from "./Formulario";
import { crearPDF } from "./pdf";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
const CONFIGS = gql`
  query {
    manifiestoConfiguraciones {
      id
      referencia
      organismo
      normativa
      declaracion
      createdAt
    }
  }
`;
export default function Administracion({ config, onRefresh, report }) {
  const client = useApolloClient(),
    { data, refetch } = useQuery(CONFIGS);
  const [c, setC] = useState(config),
    [file, setFile] = useState(null),
    [busy, setBusy] = useState(false),
    [year, setYear] = useState(""),
    [search, setSearch] = useState(""),
    [page, setPage] = useState(1),
    [createOpen, setCreateOpen] = useState(false),
    [pendingDelete, setPendingDelete] = useState(null);
  const versions = data?.manifiestoConfiguraciones || [];
  // ponytail: client pagination is enough for a short version history; move it to the API if it grows large.
  const years = [...new Set(versions.map((x) => new Date(x.createdAt).getFullYear()))]
    .filter(Number.isFinite)
    .sort((a, b) => b - a);
  const filteredVersions = versions.filter(
    (x) =>
      (!year || new Date(x.createdAt).getFullYear() === Number(year)) &&
      (!search || `${x.referencia} ${x.organismo}`.toLowerCase().includes(search.toLowerCase()))
  );
  const pageSize = 8;
  const pageCount = Math.max(1, Math.ceil(filteredVersions.length / pageSize));
  const visibleVersions = filteredVersions.slice((page - 1) * pageSize, page * pageSize);
  const run = async (fn, success = "Guardado") => {
    setBusy(true);
    try {
      await fn();
      await onRefresh();
      await refetch();
      report(success, "success");
    } catch (e) {
      report(e.message, "error");
    } finally {
      setBusy(false);
    }
  };
  const saveNewVersion = async () => {
    await run(async () => {
      await client.mutate({
        mutation: gql`
          mutation ($input: ManifiestoConfiguracionInput!) {
            guardarConfiguracionManifiesto(input: $input) {
              id
            }
          }
        `,
        variables: {
          input: {
            referencia: c.referencia,
            organismo: c.organismo,
            normativa: c.normativa,
            declaracion: c.declaracion,
            archivo: file,
          },
        },
      });
      setCreateOpen(false);
      setYear("");
      setSearch("");
      setPage(1);
    });
  };
  const downloadSample = async (version) => {
    setBusy(true);
    try {
      const resolution = await PDFDocument.load(
        await (await documentBlob("resolucion", version.id)).arrayBuffer()
      );
      const form = await PDFDocument.load(
        crearPDF({ estado: "Borrador", snapshot: { configuracion: version }, datos: empty }).output(
          "arraybuffer"
        )
      );
      const complete = await PDFDocument.create();
      for (const source of [resolution, form]) {
        const pages = await complete.copyPages(source, source.getPageIndices());
        pages.forEach((page) => complete.addPage(page));
      }
      saveBlob(
        new Blob([await complete.save()], { type: "application/pdf" }),
        `manifiesto-prueba-version-${version.id}.pdf`
      );
    } catch (e) {
      report(e.message, "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
            >
              <Typography variant="h6">Versiones del modelo</Typography>
              <SoftButton
                variant="gradient"
                color="primary"
                onClick={() => {
                  setC(config);
                  setFile(null);
                  setCreateOpen(true);
                }}
              >
                Nueva versión
              </SoftButton>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Campo
                label="Año"
                value={year}
                onChange={(value) => {
                  setYear(value);
                  setPage(1);
                }}
                options={[
                  { id: "", label: "Todos" },
                  ...years.map((y) => ({ id: String(y), label: String(y) })),
                ]}
              />
              <Campo
                label="Buscar referencia u organismo"
                value={search}
                onChange={(value) => {
                  setSearch(value);
                  setPage(1);
                }}
              />
            </Stack>
            <TableContainer sx={{ boxShadow: "none", overflowX: "auto" }}>
              <Table sx={{ minWidth: 720 }} aria-label="Versiones del modelo de manifiesto">
                <TableHead
                  sx={{
                    display: "table-header-group",
                    "& .MuiTableCell-root": { fontWeight: 700, color: "text.secondary" },
                  }}
                >
                  <TableRow>
                    <TableCell>Versión</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Referencia</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleVersions.map((version) => {
                    const active = String(config.id) === String(version.id);
                    return (
                      <TableRow
                        key={version.id}
                        sx={active ? { bgcolor: "action.selected" } : undefined}
                      >
                        <TableCell>#{version.id}</TableCell>
                        <TableCell>
                          {version.createdAt
                            ? new Date(version.createdAt).toLocaleDateString("es-AR")
                            : "—"}
                        </TableCell>
                        <TableCell>{version.referencia}</TableCell>
                        <TableCell>
                          {active ? (
                            <Typography variant="body2" color="primary" fontWeight="bold">
                              Vigente
                            </Typography>
                          ) : (
                            "Disponible"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                            alignItems="center"
                          >
                            {active ? (
                              <Typography variant="caption" color="text.secondary">
                                Activa
                              </Typography>
                            ) : (
                              <SoftButton
                                variant="gradient"
                                color="primary"
                                size="small"
                                disabled={busy}
                                onClick={() =>
                                  run(
                                    () =>
                                      client.mutate({
                                        mutation: gql`
                                          mutation ($id: ID!) {
                                            activarConfiguracionManifiesto(id: $id)
                                          }
                                        `,
                                        variables: { id: version.id },
                                      }),
                                    "Versión activada"
                                  )
                                }
                              >
                                Activar
                              </SoftButton>
                            )}
                            <Tooltip title="Descargar resolución">
                              <IconButton
                                aria-label={`Descargar resolución de la versión ${version.id}`}
                                onClick={() =>
                                  download(
                                    "resolucion",
                                    version.id,
                                    `resolucion-${version.id}.pdf`
                                  ).catch((e) => report(e.message, "error"))
                                }
                              >
                                <FileDownloadOutlined />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Descargar documento completo de prueba">
                              <span>
                                <IconButton
                                  disabled={busy}
                                  aria-label={`Descargar documento completo de prueba de la versión ${version.id}`}
                                  onClick={() => downloadSample(version)}
                                >
                                  <PictureAsPdfOutlined />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                active
                                  ? "No se puede eliminar la versión vigente"
                                  : "Eliminar versión"
                              }
                            >
                              <span>
                                <IconButton
                                  disabled={busy || active}
                                  aria-label={`Eliminar versión ${version.id}`}
                                  onClick={() => setPendingDelete(version)}
                                >
                                  <DeleteOutline />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!visibleVersions.length && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        No se encontraron versiones.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                {filteredVersions.length} versiones
              </Typography>
              {pageCount > 1 && (
                <Pagination count={pageCount} page={page} onChange={(_, value) => setPage(value)} />
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Dialog
        open={createOpen}
        onClose={() => !busy && setCreateOpen(false)}
        fullWidth
        maxWidth="lg"
        scroll="paper"
      >
        <DialogTitle>Nueva versión del modelo</DialogTitle>
        <DialogContent dividers sx={{ minHeight: { sm: 500 } }}>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Alert severity="info">
              Guardar crea una versión disponible. Luego podés activarla desde la tabla para las
              futuras presentaciones.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Campo
                  required
                  label="Referencia CDE. Resolución"
                  value={c.referencia}
                  onChange={(value) => setC({ ...c, referencia: value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Campo
                  required
                  label="Organismo"
                  value={c.organismo}
                  onChange={(value) => setC({ ...c, organismo: value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Campo
                  required
                  label="Referencias normativas"
                  value={c.normativa}
                  multiline
                  onChange={(value) => setC({ ...c, normativa: value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Campo
                  required
                  label="Declaración final"
                  value={c.declaracion}
                  multiline
                  onChange={(value) => setC({ ...c, declaracion: value })}
                />
              </Grid>
            </Grid>
            <Stack spacing={1}>
              <Typography variant="body2">
                PDF de la resolución, sin la última página del formulario. Hasta 10 MB. Si no
                seleccionás archivo, se conserva el de la versión vigente.
              </Typography>
              <input
                aria-label="PDF de páginas iniciales de la resolución"
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files[0] || null)}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <SoftButton
            variant="outlined"
            color="primary"
            disabled={busy}
            onClick={() => setCreateOpen(false)}
          >
            Cancelar
          </SoftButton>
          <SoftButton variant="gradient" color="primary" disabled={busy} onClick={saveNewVersion}>
            Guardar versión
          </SoftButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(pendingDelete)}
        onClose={() => !busy && setPendingDelete(null)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="eliminar-version-title"
        aria-describedby="eliminar-version-description"
        PaperProps={{ sx: { borderRadius: ({ borders }) => borders.borderRadius.xl } }}
      >
        <DialogTitle component="div" sx={{ p: 3, pb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <SoftTypography id="eliminar-version-title" component="h2" variant="h6">
              Eliminar versión {pendingDelete?.id}
            </SoftTypography>
            <IconButton aria-label="Cerrar" disabled={busy} onClick={() => setPendingDelete(null)}>
              <CloseOutlined />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          <Stack spacing={2}>
            <SoftTypography variant="body2" sx={{ bgcolor: "grey.100", borderRadius: 2, p: 2 }}>
              {pendingDelete?.referencia}
            </SoftTypography>
            <SoftTypography id="eliminar-version-description" variant="body2" color="text">
              Se quitará esta versión de las opciones disponibles. Si ya está vinculada a un
              manifiesto, el sistema impedirá eliminarla.
            </SoftTypography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          <SoftButton
            variant="outlined"
            color="primary"
            disabled={busy}
            onClick={() => setPendingDelete(null)}
          >
            Cancelar
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="error"
            disabled={busy}
            onClick={() =>
              run(async () => {
                await client.mutate({
                  mutation: gql`
                    mutation ($id: ID!) {
                      eliminarConfiguracionManifiesto(id: $id)
                    }
                  `,
                  variables: { id: pendingDelete.id },
                });
                setPage(1);
                setPendingDelete(null);
              }, "Versión eliminada")
            }
          >
            Eliminar
          </SoftButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
